import React, { lazy, Suspense, useEffect, useState, useRef } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import PrivateRoute from "./utils/PrivateRoute";
import { Spinner } from "./components/commonComponents/Spinner/Spinner";
import { Box } from "@mui/material";
import Footer from "./components/commonComponents/Footer/Footer";
import AndroidLocationBridge from "./components/AndroidLocationBridge";
import AndroidTokenManager from "./components/AndroidTokenManager";
import CommonSnackbar from "./components/commonComponents/Snackbar/CommonSnackbar";
import { ThemeProvider } from "./context/ThemeContext";
import travaioIcon from "./Images/svg/travaio_icon_v1.svg";
import AppInitializationError from "./components/AppInitializationError";

// Lazy load route components
const LoginPage = lazy(() => import("./features/login/Login"));
const RegistrationPage = lazy(() => import("./features/registration/Registration"));
const DashboardPage = lazy(() => import("./features/dashboard/Dashboard"));
const EnhancedDashboardPage = lazy(() => import("./features/dashboard/EnhancedDashboard"));
const Drawer = lazy(() => import("./components/layout/Header"));
const Bookings = lazy(() => import("./features/bookings/Bookings"));
const NewBooking = lazy(() => import("./features/bookings/NewBooking"));
const Vendors = lazy(() => import("./features/vendors/VendorsList"));
const AddVendorAcc = lazy(() => import("./features/vendors/AddVendorAccount"));
const Drivers = lazy(() => import("./features/drivers/DriversList"));
const AddDriver = lazy(() => import("./features/drivers/AddDriver"));
const Vehicle = lazy(() => import("./features/vehicles/VehicleList"));
const AddVehicle = lazy(() => import("./features/vehicles/AddVehicle"));
const Customers = lazy(() => import("./features/customer/customerList"));
const AddCustomer = lazy(() => import("./features/customer/addCustomer"));
const TeamMember = lazy(() => import("./features/team_member/TeamMember"));
const MapRoutes = lazy(() => import("./features/routes/RouteList"));
const AddMapRoute = lazy(() => import("./features/routes/AddEditRoute"));
const RouteConfiguration = lazy(() => import("./features/routes/RouteConfiguration"));
const RoutePriceManagement = lazy(() => import("./features/routes/RoutePriceManagement"));
const EventConfiguration = lazy(() => import("./features/routes/EventConfiguration"));
const AddTeamMember = lazy(() => import("./features/team_member/AddTeamMember"));
const Profile = lazy(() => import("./features/profile/viewProfile"));
const ResetPassword = lazy(() => import("./features/login/ResetPassword"));
const TripDetails = lazy(() => import("./features/drivers/TripDetails"));
const ETSTripsList = lazy(() => import("./features/drivers/ETSTrips/ETSTripsList"));
const ETSTripDetails = lazy(() => import("./features/drivers/ETSTrips/ETSTripDetails"));
const ETSDropTripDetails = lazy(() => import("./features/drivers/ETSTrips/ETSDropTripDetails"));
const CreateETSTrip = lazy(() => import("./features/drivers/ETSTrips/CreateETSTrip"));
const EmployeeList = lazy(() => import("./features/employees/EmployeeList"));
const AddEmployee = lazy(() => import("./features/employees/AddEmployee"));
const ViewEmployee = lazy(() => import("./features/employees/ViewEmployee"));
const EditEmployee = lazy(() => import("./features/employees/EditEmployee"));
const ErrorPage = lazy(() => import("./components/commonComponents/Error/Error"));
const ETSTripsListAdmin = lazy(() => import("./features/drivers/ETSTrips/ETSTripsListAdmin"));
const AdminTripFulfillment = lazy(() => import("./features/drivers/ETSTrips/AdminTripFulfillment"));
const VendorInvoiceView = lazy(() => import("./features/drivers/ETSTrips/VendorInvoiceView"));
const GeofencePricing = lazy(() => import("./features/geofencePricing/GeofencePricing"));
const DynamicRouteList = lazy(() => import("./features/routes/DynamicRouteList"));
const AddDynamicRoute = lazy(() => import("./features/routes/AddDynamicRoute"));
const SearchApiDebugger = lazy(() => import("./features/searchDebugger/SearchApiDebugger"));
const SearchLookupPage = lazy(() => import("./features/searchLookup/SearchLookupPage"));
const HelpSupport = lazy(() => import("./features/helpSupport/HelpSupport"));
const CreateReferralLead = lazy(() => import("./features/referralLeads/CreateReferralLead"));
const ReferralLeadsList = lazy(() => import("./features/referralLeads/ReferralLeadsList"));
const ReferralLeadDetail = lazy(() => import("./features/referralLeads/ReferralLeadDetail"));
const PayloadDecryptor = lazy(() => import("./features/payloadDecryptor/PayloadDecryptor"));
const PrivacyPolicy = lazy(() => import("./features/legal/PrivacyPolicy"));

// ─── FIX 3: ConditionalSuspense — no Spinner on login page ───────────────────
// Must be inside <Router> to use useLocation
const ConditionalSuspense = ({ children }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === "/" || location.pathname === "/register";
  // On the login page render with null fallback so no spinner flashes
  return (
    <Suspense fallback={isLoginPage ? null : <Spinner />}>
      {children}
    </Suspense>
  );
};

// ─── Inner layout component (must be inside <Router> to use useLocation) ─────
const AppContent = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === "/" || location.pathname === "/register";

  return (
    <>
      <Drawer />
      <Box
        data-scroll-container
        sx={{
          pt: isLoginPage ? 0 : "56px",
          pb: isLoginPage ? 0 : "40px",
          ml: isLoginPage ? 0 : "var(--sidebar-width, 240px)",
          transition: "margin-left 0.2s ease, background-color 0.3s ease",
          backgroundColor: "var(--page-bg)",
          minHeight: "100vh",
        }}
      >
        <Routes>
          <Route path="/" element={<LoginPage />} />
          <Route path="/register" element={<RegistrationPage />} />

          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <EnhancedDashboardPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/bookings"
            element={
              <PrivateRoute>
                <Bookings />
              </PrivateRoute>
            }
          />
          <Route
            path="/newBooking"
            element={
              <PrivateRoute>
                <NewBooking />
              </PrivateRoute>
            }
          />

          <Route
            path="/vendors"
            element={
              <PrivateRoute>
                <Vendors />
              </PrivateRoute>
            }
          />
          <Route
            path="/addAccount"
            element={
              <PrivateRoute>
                <AddVendorAcc />
              </PrivateRoute>
            }
          />

          <Route
            path="/drivers"
            element={
              <PrivateRoute>
                <Drivers />
              </PrivateRoute>
            }
          />
          <Route
            path="/addDriver"
            element={
              <PrivateRoute>
                <AddDriver />
              </PrivateRoute>
            }
          />

          <Route
            path="/vehicles"
            element={
              <PrivateRoute>
                <Vehicle />
              </PrivateRoute>
            }
          />
          <Route
            path="/addVehicle"
            element={
              <PrivateRoute>
                <AddVehicle />
              </PrivateRoute>
            }
          />

          <Route
            path="/employees"
            element={
              <PrivateRoute>
                <EmployeeList />
              </PrivateRoute>
            }
          />
          <Route
            path="/addEmployee"
            element={
              <PrivateRoute>
                <AddEmployee />
              </PrivateRoute>
            }
          />
          <Route
            path="/viewEmployee"
            element={
              <PrivateRoute>
                <ViewEmployee />
              </PrivateRoute>
            }
          />
          <Route
            path="/editEmployee"
            element={
              <PrivateRoute>
                <EditEmployee />
              </PrivateRoute>
            }
          />

          <Route
            path="/customers"
            element={
              <PrivateRoute>
                <Customers />
              </PrivateRoute>
            }
          />
          <Route
            path="/addCustomer"
            element={
              <PrivateRoute>
                <AddCustomer />
              </PrivateRoute>
            }
          />

          <Route
            path="/teamMember"
            element={
              <PrivateRoute>
                <TeamMember />
              </PrivateRoute>
            }
          />
          <Route
            path="/addTeamMember"
            element={
              <PrivateRoute>
                <AddTeamMember />
              </PrivateRoute>
            }
          />

          <Route
            path="/routes"
            element={
              <PrivateRoute>
                <MapRoutes />
              </PrivateRoute>
            }
          />
          <Route
            path="/addEditRoute"
            element={
              <PrivateRoute>
                <AddMapRoute />
              </PrivateRoute>
            }
          />
          <Route
            path="/route-price-configuration"
            element={
              <PrivateRoute>
                <RoutePriceManagement />
              </PrivateRoute>
            }
          />
          <Route
            path="/event-configuration"
            element={
              <PrivateRoute>
                <EventConfiguration />
              </PrivateRoute>
            }
          />


          <Route
            path="/viewProfile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />

          <Route path="/resetPassword" element={<ResetPassword />} />

          <Route
            path="/tripDetails/:tripId?"
            element={
              <PrivateRoute>
                <TripDetails />
              </PrivateRoute>
            }
          />

          {/* ETS Trip Routes */}
          <Route
            path="/ets-trips"
            element={
              <PrivateRoute>
                <ETSTripsList />
              </PrivateRoute>
            }
          />
          <Route
            path="/ets-trips/drop/:id"
            element={
              <PrivateRoute>
                <ETSDropTripDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/ets-trips/pickup/:id"
            element={
              <PrivateRoute>
                <ETSTripDetails />
              </PrivateRoute>
            }
          />
          <Route
            path="/ets-trips/create"
            element={
              <PrivateRoute>
                <CreateETSTrip />
              </PrivateRoute>
            }
          />
          <Route
            path="/ets-trips-admin"
            element={
              <PrivateRoute>
                <ETSTripsListAdmin />
              </PrivateRoute>
            }
          />
          <Route
            path="/ets-trips/admin-fulfill/:id"
            element={
              <PrivateRoute>
                <AdminTripFulfillment />
              </PrivateRoute>
            }
          />
          <Route
            path="/ets-trips/invoice/:id"
            element={
              <PrivateRoute>
                <VendorInvoiceView />
              </PrivateRoute>
            }
          />

          <Route
            path="/geofence-pricing"
            element={
              <PrivateRoute>
                <GeofencePricing />
              </PrivateRoute>
            }
          />

          {/* Dynamic Routes */}
          <Route
            path="/dynamic-routes"
            element={
              <PrivateRoute>
                <DynamicRouteList />
              </PrivateRoute>
            }
          />
          <Route
            path="/add-dynamic-route"
            element={
              <PrivateRoute>
                <AddDynamicRoute />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-dynamic-route/:id"
            element={
              <PrivateRoute>
                <AddDynamicRoute />
              </PrivateRoute>
            }
          />

          <Route
            path="/search-debugger"
            element={
              <PrivateRoute>
                <SearchApiDebugger />
              </PrivateRoute>
            }
          />

          <Route
            path="/search-lookup"
            element={
              <PrivateRoute>
                <SearchLookupPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/help-support"
            element={
              <PrivateRoute>
                <HelpSupport />
              </PrivateRoute>
            }
          />

          <Route
            path="/referral-leads/create"
            element={
              <PrivateRoute>
                <CreateReferralLead />
              </PrivateRoute>
            }
          />

          <Route
            path="/referral-leads"
            element={
              <PrivateRoute>
                <ReferralLeadsList />
              </PrivateRoute>
            }
          />

          <Route
            path="/referral-leads/:id"
            element={
              <PrivateRoute>
                <ReferralLeadDetail />
              </PrivateRoute>
            }
          />


          <Route
            path="/payload-decryptor"
            element={
              <PrivateRoute>
                <PayloadDecryptor />
              </PrivateRoute>
            }
          />

          <Route
            path="/privacy-policy"
            element={
              <PrivateRoute>
                <PrivacyPolicy />
              </PrivateRoute>
            }
          />

          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </Box>
    </>
  );
};

// ─── Footer: hidden on login page ─────────────────────────────────────────────
const ConditionalFooter = () => {
  const location = useLocation();
  if (location.pathname === "/" || location.pathname === "/register") return null;
  return <Footer />;
};

// ─── Root App ─────────────────────────────────────────────────────────────────
const App = () => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [globalToastOpen, setGlobalToastOpen] = useState(false);
  const [globalToastMessage, setGlobalToastMessage] = useState("");
  const [globalToastStatus, setGlobalToastStatus] = useState("error");
  const toastQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);

  // Set app title and favicon
  useEffect(() => {
    document.title = "TRAVAIO - Booking Management";

    const favicon =
      document.querySelector("link[rel='icon']") ||
      document.createElement("link");

    favicon.rel = "icon";
    favicon.type = "image/svg+xml";
    favicon.href = travaioIcon;

    if (!favicon.parentNode) {
      document.head.appendChild(favicon);
    }
  }, []);

  // Migrate all data from sessionStorage to localStorage (for existing users)
  useEffect(() => {
    const sessionToken = sessionStorage.getItem("token");
    const localToken = localStorage.getItem("token");
    if (sessionToken && !localToken) {
      console.log("Migrating token from sessionStorage to localStorage");
      localStorage.setItem("token", sessionToken);
      sessionStorage.removeItem("token");
      setToken(sessionToken);
    } else if (localToken) {
      setToken(localToken);
    }

    const sessionUserInfo = sessionStorage.getItem("userInfo");
    const localUserInfo = localStorage.getItem("userInfo");
    if (sessionUserInfo && !localUserInfo) {
      console.log("Migrating userInfo from sessionStorage to localStorage");
      localStorage.setItem("userInfo", sessionUserInfo);
      sessionStorage.removeItem("userInfo");
    }

    const sessionUserId = sessionStorage.getItem("userId");
    const localUserId = localStorage.getItem("userId");
    if (sessionUserId && !localUserId) {
      console.log("Migrating userId from sessionStorage to localStorage");
      localStorage.setItem("userId", sessionUserId);
      sessionStorage.removeItem("userId");
    }

    const sessionSidebarPermission = sessionStorage.getItem("sidebarPermission");
    const localSidebarPermission = localStorage.getItem("sidebarPermission");
    if (sessionSidebarPermission && !localSidebarPermission) {
      console.log("Migrating sidebarPermission from sessionStorage to localStorage");
      localStorage.setItem("sidebarPermission", sessionSidebarPermission);
      sessionStorage.removeItem("sidebarPermission");
    }
  }, []);

  // Listen for storage changes (token updates)
  useEffect(() => {
    const handleStorageChange = () => {
      const currentToken = localStorage.getItem("token");
      setToken(currentToken);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("tokenUpdated", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tokenUpdated", handleStorageChange);
    };
  }, []);

  // Process toast queue
  const processToastQueue = () => {
    if (isProcessingQueueRef.current || toastQueueRef.current.length === 0) return;

    isProcessingQueueRef.current = true;
    const nextMessage = toastQueueRef.current.shift();

    setGlobalToastMessage(nextMessage);
    setGlobalToastStatus("error");
    setGlobalToastOpen(true);
  };

  // Handle toast close
  const handleToastClose = () => {
    setGlobalToastOpen(false);
    isProcessingQueueRef.current = false;

    setTimeout(() => {
      processToastQueue();
    }, 300);
  };

  // Listen for 403 errors from API client
  useEffect(() => {
    const handle403Error = (event) => {
      const message = event.detail.message || "Access to this endpoint is not allowed";

      toastQueueRef.current.push(message);

      if (!isProcessingQueueRef.current && !globalToastOpen) {
        processToastQueue();
      }
    };

    window.addEventListener("api403Error", handle403Error);

    return () => {
      window.removeEventListener("api403Error", handle403Error);
    };
  }, [globalToastOpen]);

  return (
    <ThemeProvider>
    <Router>
      <AndroidLocationBridge>
        <AndroidTokenManager />
        <Box>
          {/* FIX 3: Use ConditionalSuspense instead of Suspense
              ConditionalSuspense shows null (no spinner) on the login page
              and shows the normal Spinner on all other pages */}
          <ConditionalSuspense>
            <AppContent />
          </ConditionalSuspense>
        </Box>
        <ConditionalFooter />
        <CommonSnackbar
          message={globalToastMessage}
          status={globalToastStatus}
          open={globalToastOpen}
          setOpen={setGlobalToastOpen}
          handleClose={handleToastClose}
        />
        <AppInitializationError />
      </AndroidLocationBridge>
    </Router>
    </ThemeProvider>
  );
};

export default App;